// One shot as its own page (UX line 19.10.2026: no shot opens 1400 px long inside a list any more): its figures and
// taste on top, Claude right under them (22.10.2026), the curve with a comparison, the key figures in groups, tags and
// note, at the end reference and deletion. Opened from "Letzte Bezüge", the history and "Letzter Bezug" of the machine tab (#bezug/bezug/<id>).
// Decisions in core/screens/brew.ts.
import { useEffect, useId, useState } from 'react';
import type { ShotItem } from '@brewcore/app-contract';
import {
  cleanNote,
  noteToSave,
  reasonText,
  shotBeanParts,
  shotDetail,
  shotFigures,
  shotLine,
  shotNeighbours,
  shotTitle,
  tasteAfterTap,
  tasteTagsAfterTap,
  type ShotDetailView,
} from '../../core/index.js';
import {
  Button,
  Card,
  CardHeader,
  fitLineOf,
  IconButton,
  LaneChart,
  NoteLine,
  Pending,
  ProgressBar,
  RateRow,
} from '../components/index.js';
import { useMachine, useStore } from '../machine-context.js';
import { AskSheet, Choice, SubScreen } from './parts.js';
// Claude support (src/ui/ai): the card under the rating; a sour or bitter rating asks Claude (Dominik 22.10.2026)
import { ClaudeShot } from '../ai/ClaudeShot.js';
import { useAssistant } from '../ai/use-assistant.js';

/**
 * The page of one shot (prototype "Ein Bezug"): its time as the title, bean and day below, arrows to the shot before
 * and after it; from the last shots, a loaded curve or the history; loaded on its own when linked to.
 */
export function ShotPage({
  id,
  onBack,
  onOpen,
}: {
  id: string;
  onBack: () => void;
  /** another shot (the arrows) */
  onOpen?: (id: string) => void;
}) {
  const { ctx, client, machineId } = useMachine();
  const store = client.shots(machineId);
  const sv = useStore(store);
  const hv = useStore(client.shotHistory(machineId));
  const beans = useStore(client.beans(machineId));
  const shot =
    sv.items.find((s) => s.id === id) ?? sv.curves[id] ?? hv.items.find((s) => s.id === id) ?? null;
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    if (shot) return;
    let live = true;
    void store.curve(id).then((r) => {
      if (live && !r) setMissing(true);
    });
    return () => {
      live = false;
    };
  }, [id, shot, store]);
  const list = sv.items.some((s) => s.id === id) ? sv.items : hv.items;
  const near = shotNeighbours(id, list);
  const arrows = onOpen && (
    <span className="shot-arrows">
      <IconButton
        icon="chevronLeft"
        label={ctx.tr('prevShot')}
        disabled={!near.older}
        onClick={() => near.older && onOpen(near.older)}
        data-testid="shot-prev"
      />
      <IconButton
        icon="chevronRight"
        label={ctx.tr('nextShot')}
        disabled={!near.newer}
        onClick={() => near.newer && onOpen(near.newer)}
        data-testid="shot-next"
      />
    </span>
  );
  return (
    <SubScreen
      tab="bezug"
      title={shot ? shotTitle(ctx, shot) : ctx.tr('shotTitle')}
      sub={shot ? fitLineOf(shotBeanParts(ctx, shot, beans)) : undefined}
      onBack={onBack}
      right={arrows}
      testId="shot-page"
    >
      {shot ? (
        <ShotDetail key={shot.id} shot={shot} onDeleted={onBack} />
      ) : (
        <Card className="tab-card">
          {missing ? <p className="hint">{ctx.tr('shotMissing')}</p> : <Pending label={ctx.tr('loading')} />}
        </Card>
      )}
    </SubScreen>
  );
}

export function ShotDetail({ shot, onDeleted }: { shot: ShotItem; onDeleted: () => void }) {
  const { ctx, client, machineId } = useMachine();
  const { assistant } = useAssistant();
  const store = client.shots(machineId);
  const sv = useStore(store);
  const beans = client.beans(machineId);
  const bv = useStore(beans);
  const [deleting, setDeleting] = useState(false);
  const d: ShotDetailView = shotDetail(ctx, shot, sv, undefined, bv);
  const f = shotFigures(ctx, shot);
  const line = shotLine(ctx, shot);
  const loadKey = d.load.join(',');
  const titleId = useId();
  useEffect(() => {
    for (const id of loadKey ? loadKey.split(',') : []) void store.curve(id);
  }, [loadKey, store]);
  return (
    <div className="shot-page-body" data-testid="shot-detail">
      {/* the key figures with their names (prototype "Ein Bezug"), the app's reading, the rating */}
      <Card className="tab-card" data-testid="shot-head">
        <dl className="fact-row is-key" data-testid="shot-figures-head">
          <div className="fact">
            <dt>{ctx.tr('shotTime')}</dt>
            <dd className="num">
              {line.timeShort}
              {f.piText && <small className="dev">{f.piText}</small>}
            </dd>
          </div>
          {f.weightText && (
            <div className="fact">
              <dt>{ctx.tr('shotWeight')}</dt>
              <dd className="num">{f.weightText}</dd>
            </div>
          )}
          {f.ratioText && (
            <div className="fact">
              <dt>{ctx.tr('ratio')}</dt>
              <dd className="num">{f.ratioText}</dd>
            </div>
          )}
        </dl>
        {d.readings.map((n) => (
          <NoteLine key={n.text} ok={n.ok}>
            {n.text}
          </NoteLine>
        ))}
        <RateRow
          label={d.taste.question}
          options={d.taste.options}
          onPick={(v) => {
            const rating = tasteAfterTap(shot.rating, v);
            const saving = store.rate(shot.id, rating);
            assistant.rated(machineId, shot.id, rating, ctx.lang, saving);
          }}
          testId="shot-taste"
        />
      </Card>
      {/* Claude: the suggestion with "Übernehmen", "Weiter fragen" (src/ui/ai) */}
      <ClaudeShot shot={shot} />
      <Card className="tab-card shot-curve" data-testid="shot-curve">
        {d.curve === undefined ? (
          <Pending label={ctx.tr('loading')} />
        ) : d.lanes ? (
          <LaneChart
            view={d.lanes}
            info={{ text: ctx.tr('scrubHint'), label: ctx.tr('infoShow') }}
            testId="shot-chart"
          />
        ) : (
          <p className="hint">{ctx.tr('noCurve')}</p>
        )}
      </Card>
      {d.phases.length > 0 && (
        <Card className="tab-card" data-testid="shot-phases">
          <CardHeader title={ctx.tr('phases')} />
          {d.phases.map((p) => (
            <div key={p.key} className="phase">
              <div className="phase-head">
                <span className="row-label">{p.name}</span>
                <span className="phase-value num">{p.value}</span>
              </div>
              <ProgressBar fraction={p.fraction} />
              {p.hint && <span className="hint">{p.hint}</span>}
            </div>
          ))}
        </Card>
      )}
      {d.settings.length > 0 && (
        <Card className="tab-card" data-testid="shot-settings">
          <CardHeader title={ctx.tr('shotSettingsTitle')} />
          <dl className="fact-grid" data-testid="shot-figures">
            {d.settings.map((x) => (
              <div key={x.label} className="fact">
                <dt>{x.label}</dt>
                <dd className="num">{x.value}</dd>
              </div>
            ))}
          </dl>
        </Card>
      )}
      <Card className="tab-card" data-testid="shot-tags-card">
        <CardHeader title={d.taste.tagsLabel} />
        <Choice
          label={d.taste.tagsLabel}
          options={d.taste.tags}
          multi
          onPick={(tag) => void store.setTags(shot.id, tasteTagsAfterTap(shot.tags, tag))}
          testId="shot-tags"
        />
        <NoteEditor shot={shot} />
      </Card>
      <div className="page-actions">
        <Button
          variant="secondary"
          block
          onClick={() =>
            void (d.referenceBean
              ? beans.setReference(d.referenceBean, d.isReference ? null : shot.id)
              : store.setReference(d.isReference ? null : shot.id))
          }
          data-testid="shot-reference"
        >
          {d.referenceLabel}
        </Button>
        <button
          type="button"
          className="link-btn danger-link"
          onClick={() => setDeleting(true)}
          data-testid="shot-delete"
        >
          {d.deleteLabel}
        </button>
      </div>
      {deleting && (
        <AskSheet
          ask={d.deleteAsk}
          titleId={titleId}
          onNo={() => setDeleting(false)}
          onYes={() => {
            setDeleting(false);
            void store.remove(shot.id).then((r) => r.ok && onDeleted());
          }}
          testId="shot-delete"
        />
      )}
    </div>
  );
}

/** Note of a shot (at most 500 characters); a conflict with another device reloads and says so. */
function NoteEditor({ shot }: { shot: ShotItem }) {
  const { ctx, client, machineId } = useMachine();
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState('');
  const id = useId();
  const value = draft ?? shot.note ?? '';
  const changed = draft !== null && draft.trim() !== (shot.note ?? '');
  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>
        {ctx.tr('note')}
      </label>
      <textarea
        id={id}
        className="field-input field-area"
        rows={2}
        maxLength={500}
        value={value}
        placeholder={ctx.tr('notePh')}
        onChange={(e) => setDraft(cleanNote(e.target.value))}
        data-testid="shot-note"
      />
      {changed && (
        <Button
          size="sm"
          onClick={async () => {
            const r = await client.shots(machineId).setNote(shot.id, noteToSave(draft ?? ''), shot.cloudRev);
            if (r.ok) {
              setDraft(null);
              setError('');
            } else setError(reasonText(ctx.lang, r.error.code));
          }}
          data-testid="shot-note-save"
        >
          {ctx.tr('noteSave')}
        </Button>
      )}
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
